<script>
import appConfig from '@src/app.config'
import pbiService from '@services/powerbi.js'
import moment from 'moment'

export default {
  data() {
    return {
      reportId: this.$route.params.reportId || process.env.VUE_APP_REPORT_ID_DEFAULT_PRINT,
      showColumnFilter: this.$route.query.customize || false,
      exportId: undefined,
      generatingReport: false,
      loadingReport: false,
      errorMessage: undefined,
      reportName: "",
      reportDate: undefined,
      reportHeaders: [],
      reportData: undefined,
      tryCount: 0,
      tryTime: 0,
      sortColumn: undefined,
      sortDirection: 1,
      isSorting: false
    }
  },
  page: {
    title: 'BGT',
    meta: [{ name: 'description', content: appConfig.description }],
  },
  computed: {

  },
  async mounted() {
    pbiService.requestReportExport(this.reportId).then(results => {
      this.generatingReport = true
      this.exportId = results.data.value.id

      this.getExportStatus()
    },
    (error) => {
      this.errorMessage = "Failed to request report."
      console.error(error)
    })
  },
  methods: {
    getExportStatus() {
      this.tryCount++;
      pbiService.getExportStatus(this.reportId, this.exportId).then(results => {
        this.requestId = results.data.value.id
        this.reportName = results.data.value.reportName
        this.reportDate = new Date(results.data.value.createdDateTime)
        if (["NotStarted", "Running", 1, 2].includes(results.data.value.status)) {
          if (this.tryCount >= 50) {
            this.generatingReport = false;
            var timeInSeconds =  ((this.tryCount*(this.tryCount+1)/2)/10)+(2*this.tryCount)
            this.errorMessage = "The Report Generation Request timed out after " + timeInSeconds + " seconds.  The report data may be too large."
          } else {
            // increase the time between polling by 0.1s * the number of attempts this decreases the api calls
            var retryTime = 2000+(this.tryCount*100)
            this.tryTime = this.tryTime + (retryTime/1000)
            setTimeout(this.getExportStatus, retryTime)
          }
        } else {
          this.generatingReport = false;
          if (["Succeeded", 3].includes(results.data.value.status)) {
            this.loadingReport = true;
            this.getExportData();
          } else {
            this.errorMessage = "Failed to generate report.";
          }
        }
      },
      (error) => {
        this.generatingReport = false;
        this.errorMessage = "Failed to generate report.";
        console.error(error);
      })
    },
    getExportData() {
      pbiService.downloadExport(this.reportId, this.exportId).then(results => {
        this.loadingReport = false;
        this.reportHeaders = Object.keys(results.data.value.records[0]).map((k,i) => {
          var firstRecord = results.data.value.records.find(r => r[k] && r[k] !== '');
          return {
            show: true,
            title: k,
            isDate: firstRecord && isNaN(firstRecord[k]) && moment(firstRecord[k]).isValid()
          }
        });
        this.reportData = results.data.value.records;
      },
      (error) => {
        this.loadingReport = false;
        this.errorMessage = "Failed to load report.";
        console.error(error);
      })
    },
    swapReportHeader(header) {
      header.show = !header.show
    },
    print() {
      window.print();
    },
    dynamicSort(property, sortDirection, isDate) {
      return function (a,b) {
        var result;
        if (isDate){
          result = moment(a[property]).isAfter(b[property]) ? -1 : moment(a[property]).isBefore(b[property]) ? 1 : 0;
        } else {
          result = (a[property] < b[property]) ? -1 : (a[property] > b[property]) ? 1 : 0;
        }
        return result * sortDirection;
      }
    },
    handleHeaderClick(header) {
      if (header) {
        if (header.title === this.sortColumn) {
          this.sortDirection *= -1;
        } else {
          this.sortDirection = 1;
        }
        this.sortColumn = header.title;
        var isDate = header.isDate;
        this.isSorting = true;
        this.reportData.sort(this.dynamicSort(this.sortColumn, this.sortDirection, isDate));
      }
    }
  }
}
</script>

<template>
  <div>
    <div v-if="generatingReport" class="loadingMessage">
      <div>
        Generating Report
        <div>
          <div class="lds-ellipsis">
            <div></div><div></div><div></div><div></div>
          </div>
        </div>
        <small>This may take a moment</small>
      </div>
    </div>

    <div v-if="loadingReport" class="loadingMessage">
      <div>
        Loading Data
        <div>
          <div class="lds-ellipsis">
            <div></div><div></div><div></div><div></div>
          </div>
        </div>
        <small>Almost done</small>
      </div>
    </div>

    <div v-if="errorMessage" class="loadingMessage">
      <div>
        We hit a snag ...
        <div>
          <div>
            <span class="warn warning"></span>
          </div>
        </div>
        <small>It looks like we ran into a problem loading this report.  Please refresh the page to try again or contact support.</small><br/>
        <small>{{errorMessage}}</small>
      </div>
    </div>
    <div v-if="reportData">
      <h2>
        <button class="print-button" @click="print">Print</button>
        {{ reportName }} <br />
        <small>{{ reportDate && reportDate.toLocaleString() }}</small>
      </h2>
      <div v-if="showColumnFilter" class="filters">
        <div>
          <h4>Remove these columns:<!-- <button style="float:right" @click="print">Print</button> --></h4>
          <div class="columns">
            <div v-for="(header, headerIndex ) in reportHeaders" :key="`show-header-${headerIndex}`" :class="{ active: header.show }" class="column"><span @click="swapReportHeader(header)">{{header.title}}</span></div>
            <div style="text-align:right"><span class="legend"><span class="column active">&nbsp;</span> Print Column</span> <span class="legend"><span class="column">&nbsp;</span> Remove Column</span></div>
          </div>
        </div>
      </div>
      <table>
        <thead>
          <tr>
            <th v-for="(header, headerIndex ) in reportHeaders.filter(h => h.show)" :key="`header-${headerIndex}`" @click="$event => handleHeaderClick(header)">
              <span :class="{ 'sort-by': !sortColumn || sortColumn === header.title, 'sort-up': sortColumn && sortDirection > 0, 'sort-down': sortColumn && sortDirection < 0 }">{{header.title}}</span>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(row, index) in reportData" :key="`row-${index}`">
            <!-- eslint-disable-next-line -->
            <td v-for="(header, i ) in reportHeaders.filter(h => h.show)" :key="`column-${i}`">
              <span v-if="header.isDate">{{ row[header.title] | formatDate }}</span>
              <span v-else v-html="row[header.title].replaceAll(';','&nbsp;<br/>')"></span>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<style lang="scss" scoped>
h2 {
  text-align: center;

  small {
    font-size: .7em;
  }
}

.print-button {
  float: right;
  margin: 20px 10px;
  font-size: 22px;
  font-weight: 600;
  padding: 3px 15px;
}

@media print {
  table > tr:nth-child(odd) {
    -webkit-print-color-adjust:exact;
    background: lightgray !important;
    print-color-adjust: exact;
  }
}

table {
  margin: 10px;

  th {
    cursor: pointer;
  }

  th, td {
    padding: 2px 7px;
    border: 1px solid black;
  }

  thead tr {
    background: #dddddd;
  }

  tbody tr:nth-child(odd) {
    background: #eeeeee;
  }
}

.filters {
  padding: 10px;
  margin: 5px;
  border: 1px gray solid;
  vertical-align: top;

  @media print {
    display: none;
  }

  .columns {
    .column {
      display:inline-block;
      padding: 2px 10px;
      margin: 5px 5px 10px;
      border-radius: 7px;
      background: lightgray;
      font-weight: bold;
      cursor: pointer;
      min-height: 20px;
      min-width: 40px;


      &.active {
        background: darkblue;
        color: white;
      }
    }
  }

  .legend {
    font-weight: 600;

    .column {
      min-height: 20px;
      min-width: 50px;
      margin: 0 0 5px 20px;
      vertical-align: middle;
    }
  }
}

.half {
  display: inline-block;
  width: 49%;
}

.loadingMessage {
  height: 100vh;
  position: relative;
  margin: 0 auto;
  max-width: 600px;

  > div {
    font-size: 2vw;
    color: gray;
    text-align: center;
    width: 100%;
    margin: 0;
    position: absolute;
    top: 50%;
    -ms-transform: translateY(-50%);
    transform: translateY(-50%);

    small {
      font-size: 1vw
    }
  }
  .lds-ellipsis {
    display: inline-block;
    position: relative;
    width: 80px;
    height: 80px;
    margin-top: 30px;
  }
  .lds-ellipsis div {
    position: absolute;
    top: 33px;
    width: 13px;
    height: 13px;
    border-radius: 50%;
    background: gray;
    animation-timing-function: cubic-bezier(0, 1, 1, 0);
  }
  .lds-ellipsis div:nth-child(1) {
    left: 8px;
    animation: lds-ellipsis1 0.6s infinite;
  }
  .lds-ellipsis div:nth-child(2) {
    left: 8px;
    animation: lds-ellipsis2 0.6s infinite;
  }
  .lds-ellipsis div:nth-child(3) {
    left: 32px;
    animation: lds-ellipsis2 0.6s infinite;
  }
  .lds-ellipsis div:nth-child(4) {
    left: 56px;
    animation: lds-ellipsis3 0.6s infinite;
  }
  @keyframes lds-ellipsis1 {
    0% {
      transform: scale(0);
    }
    100% {
      transform: scale(1);
    }
  }
  @keyframes lds-ellipsis3 {
    0% {
      transform: scale(1);
    }
    100% {
      transform: scale(0);
    }
  }
  @keyframes lds-ellipsis2 {
    0% {
      transform: translate(0, 0);
    }
    100% {
      transform: translate(24px, 0);
    }
  }
  .warn,
  .warn::before,
  .warn::after
  {
    position: relative;
    padding: 0;
    margin: 0;
  }

  .warn {
    font-size: 60px;
    font-weight: bold;
    color: transparent;
    margin: 20px 0;
  }

  .warn.warning {
    display: inline-block;

    top: 0.225em;

    width: 1.15em;
    height: 1.15em;

    overflow: hidden;
    border: none;
    background-color: transparent;
    border-radius: 0.625em;
  }

  .warn.warning::before {
    content: "";
    display: block;
    top: -0.08em;
    left: 0.0em;
    position: absolute;
    border: transparent 0.6em solid;
    border-bottom-color: #fd3;
    border-bottom-width: 1em;
    border-top-width: 0;
    box-shadow: #999 0 1px 1px;
  }

  .warn.warning::after {
    display: block;
    position: absolute;
    top: 0.55em;
    left: 0;
    width: 100%;
    padding: 0 1px;
    text-align: center;
    font-family: "Garamond";
    content: "!";
    font-size: 0.65em;
    font-weight: bold;
    color: gray;
  }
}
.sort-by {
	padding-right: 18px;
	position: relative;
  display: inline-block;
}
.sort-by:before,
.sort-by:after {
	border: 4px solid transparent;
	content: "";
	display: block;
	height: 0;
	right: 5px;
	top: 50%;
	position: absolute;
	width: 0;
}
.sort-by:not(.sort-down):before {
	border-bottom-color: #666;
	margin-top: -9px;
}
.sort-by:not(.sort-up):after {
	border-top-color: #666;
	margin-top: 1px;
}
</style>
